import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  ActivityIndicator,
  RefreshControl,
  Modal
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { 
  getAccountingSummary,
  getAccountingTransactions,
  getDriverBalances,
  initializeDriverBalances,
  importCareemPaymentData,
  importUberCaptainData,
  importTrafficFinesData,
  importSalikData,
  recalculateAllDriverBalances
} from '../../api/supabase';
import { getDriverRentDetails, getDriverRentSummary } from '../../api/driverRentDetails';
import { getDetailedDriverBreakdown } from '../../utils/detailedDriverBreakdown';
import { DriverPaymentModal } from '../../components/modals/DriverPaymentModal';
import DriverPaymentModalEnhanced from '../../components/DriverPaymentModal';
import type { AccountingSummary, AccountingTransaction, DriverBalance } from '../../types';

const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth > 768;

interface AccountingScreenProps {
  navigation?: any;
}

export const AccountingScreen: React.FC<AccountingScreenProps> = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'import' | 'transactions' | 'balances'>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState<AccountingSummary>({
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    pendingTransactions: 0,
    driversWithNegativeBalance: 0,
    monthlyRevenue: 0,
    monthlyExpenses: 0
  });
  const [transactions, setTransactions] = useState<AccountingTransaction[]>([]);
  const [driverBalances, setDriverBalances] = useState<DriverBalance[]>([]);
  const [selectedBalance, setSelectedBalance] = useState<DriverBalance | null>(null);
  const [balanceModalVisible, setBalanceModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [rentDetails, setRentDetails] = useState<any>(null);
  const [loadingRentDetails, setLoadingRentDetails] = useState(false);
  const [showRentBreakdown, setShowRentBreakdown] = useState(false);
  const [showSalikBreakdown, setShowSalikBreakdown] = useState(false);
  const [showFinesBreakdown, setShowFinesBreakdown] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const handleImportRealData = async () => {
    Alert.alert(
      'Import Real Business Data',
      'This will import your actual Careem, Uber, and fine data from CSV files. Which data would you like to import?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Import All Data', 
          onPress: async () => {
            try {
              setLoading(true);
              await initializeDriverBalances();
              
              // Import each data file
              let totalImported = 0;
              const results = [];

              // Note: In a real implementation, you would read the actual CSV files
              // For now, we'll show how the import would work
              Alert.alert(
                'Ready to Import',
                'To import your real data:\n\n1. Go to the Import tab\n2. Select your CSV files\n3. Choose the data type (Careem, Uber, Fines)\n4. Upload and process\n\nThis will import your actual business transactions instead of sample data.'
              );
              
              loadData();
            } catch (error) {
              console.error('Error importing data:', error);
              Alert.alert('Error', 'Failed to import data: ' + (error as Error).message);
            } finally {
              setLoading(false);
            }
          }
        },
        { 
          text: 'Initialize Only', 
          onPress: async () => {
            try {
              setLoading(true);
              await initializeDriverBalances();
              Alert.alert('Success', 'Driver balances initialized. Ready for data import.');
              loadData();
            } catch (error) {
              console.error('Error initializing balances:', error);
              Alert.alert('Error', 'Failed to initialize driver balances');
            } finally {
              setLoading(false);
            }
          }
        }
      ]
    );
  };

  const handleRecalculateBalances = async () => {
    Alert.alert(
      'Recalculate All Balances',
      'This will recalculate all driver balances based on existing transactions. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Recalculate', 
          onPress: async () => {
            try {
              setLoading(true);
              const result = await recalculateAllDriverBalances();
              Alert.alert(
                'Success', 
                `Recalculated balances for ${result.success} drivers`
              );
              loadData();
            } catch (error) {
              console.error('Error recalculating balances:', error);
              Alert.alert('Error', 'Failed to recalculate balances');
            } finally {
              setLoading(false);
            }
          }
        }
      ]
    );
  };

  const loadData = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      console.log('🔄 Loading accounting data...');

      const [summaryData, transactionsData, balancesData] = await Promise.all([
        getAccountingSummary({ 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate 
        }),
        getAccountingTransactions({ 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate 
        }),
        getDriverBalances()
      ]);

      console.log('📊 Loaded data:');
      console.log('- Summary:', summaryData);
      console.log('- Transactions:', transactionsData?.length || 0);
      console.log('- Driver Balances:', balancesData?.length || 0);
      
      if (balancesData && balancesData.length > 0) {
        console.log('- First 3 balances:');
        balancesData.slice(0, 3).forEach((b, index) => {
          console.log(`  ${index + 1}. ${b.driver?.name}:`);
          console.log(`     Current Balance: AED ${b.currentBalance}`);
          console.log(`     Total Earned: AED ${(b.totalCareemIncome || 0) + (b.totalUberIncome || 0)}`);
          console.log(`     Careem: AED ${b.totalCareemIncome}`);
          console.log(`     Uber: AED ${b.totalUberIncome}`);
          console.log(`     Fines: AED ${b.totalFines}`);
          console.log(`     Rent: AED ${b.totalRentCharged}`);
          console.log(`     Salik: AED ${b.totalSalik}`);
        });
        const total = balancesData.reduce((sum, b) => sum + (b.currentBalance || 0), 0);
        console.log('- Total balance:', total);
      }

      setSummary(summaryData);
      setTransactions(transactionsData as AccountingTransaction[]);
      setDriverBalances(balancesData);
    } catch (error) {
      console.error('❌ Error loading accounting data:', error);
      Alert.alert('Error', 'Failed to load accounting data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleBalancePress = async (balance: DriverBalance) => {
    console.log('🔍 Balance data being passed to modal:', {
      driverId: balance.driverId,
      currentBalance: balance.currentBalance,
      totalEarned: balance.totalEarned, // Use existing value
      calculatedEarned: (balance.totalCareemIncome || 0) + (balance.totalUberIncome || 0), // Compare with calculation
      totalCareemIncome: balance.totalCareemIncome,
      totalUberIncome: balance.totalUberIncome,
      totalFines: balance.totalFines,
      totalRentCharged: balance.totalRentCharged,
      totalSalik: balance.totalSalik,
      totalOwed: balance.totalOwed, // Use existing value
      calculatedOwed: balance.totalRentCharged + balance.totalFines + balance.totalSalik, // Compare with calculation
      driverName: balance.driver?.name
    });
    
    setSelectedBalance(balance);
    setBalanceModalVisible(true);
    setLoadingRentDetails(true);
    setRentDetails(null);
    
    // Reset breakdown toggles
    setShowRentBreakdown(false);
    setShowSalikBreakdown(false);
    setShowFinesBreakdown(false);

    try {
      console.log('🔍 Loading comprehensive rent details and breakdown for driver:', balance.driverId);
      
      // Load rent details using actual assignment data structure
      const rentData = await getDriverRentDetails(balance.driverId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      // Load detailed breakdown
      const detailedBreakdown = await getDetailedDriverBreakdown(balance.driverId);
      
      // Update the balance with detailed breakdown but preserve original balance values
      const updatedBalance = {
        ...balance, // Keep all original balance data
        detailedBreakdown // Add detailed breakdown without overriding existing totals
      };
      
      // Check for discrepancies between database totals and detailed breakdown totals
      const dbTotalEarned = (balance.totalCareemIncome || 0) + (balance.totalUberIncome || 0);
      const breakdownTotalEarned = detailedBreakdown.income.total;
      
      console.log('📊 Detailed breakdown loaded:', {
        rentItems: detailedBreakdown.rent.details.length,
        salikItems: detailedBreakdown.salik.details.length,
        fineItems: detailedBreakdown.fines.details.length,
        incomeItems: detailedBreakdown.income.details.length,
        originalBalance: balance.currentBalance,
        preservedBalance: updatedBalance.currentBalance,
        originalTotalEarned: balance.totalEarned,
        preservedTotalEarned: updatedBalance.totalEarned,
        originalTotalOwed: balance.totalOwed,
        preservedTotalOwed: updatedBalance.totalOwed,
        // Comparison between database and breakdown calculations
        dbTotalEarned,
        breakdownTotalEarned,
        earningsMatch: Math.abs(dbTotalEarned - breakdownTotalEarned) < 0.01,
        dbCareemIncome: balance.totalCareemIncome,
        dbUberIncome: balance.totalUberIncome
      });
      
      setSelectedBalance(updatedBalance);
      
      console.log('📊 Rent details loaded:', {
        detailsCount: rentData.rentDetails.length,
        totalAmount: rentData.summary.totalAmount,
        totalDays: rentData.summary.totalDays,
        uniqueVehicles: rentData.summary.uniqueVehicles,
        activeAssignments: rentData.summary.activeAssignments
      });
      
      setRentDetails(rentData);
    } catch (error) {
      console.error('❌ Error loading rent details:', error);
      // Show basic modal even if rent details fail
    } finally {
      setLoadingRentDetails(false);
    }
  };

  const handlePaymentRecorded = async () => {
    // Refresh the data after payment is recorded
    await loadData();
    
    // If the balance modal is still open, refresh the selected balance
    if (selectedBalance) {
      const updatedBalances = await getDriverBalances();
      const updatedBalance = updatedBalances.find(b => b.driverId === selectedBalance.driverId);
      if (updatedBalance) {
        // Preserve the detailed breakdown when updating the balance
        const balanceWithBreakdown = {
          ...updatedBalance,
          detailedBreakdown: selectedBalance.detailedBreakdown // Keep the existing breakdown
        };
        setSelectedBalance(balanceWithBreakdown);
      }
    }
  };

  const handleOpenPaymentModal = () => {
    setPaymentModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return theme.colors.success;
      case 'pending': return theme.colors.warning;
      case 'cancelled': return theme.colors.error;
      default: return theme.colors.gray400;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'income': return theme.colors.success;
      case 'expense': return theme.colors.error;
      case 'fine': return theme.colors.warning;
      case 'salik': return theme.colors.info;
      default: return theme.colors.gray400;
    }
  };

  const renderSummaryCard = (title: string, value: string, icon: string, color: string, trend?: 'up' | 'down') => (
    <View style={[styles.summaryCard, { borderLeftColor: color }]}>
      <View style={styles.summaryContent}>
        <View style={styles.summaryHeader}>
          <Text style={styles.summaryTitle}>{title}</Text>
          <Ionicons name={icon as any} size={24} color={color} />
        </View>
        <Text style={[styles.summaryValue, { color }]}>{value}</Text>
        {trend && (
          <View style={styles.trendContainer}>
            <Ionicons 
              name={trend === 'up' ? 'trending-up' : 'trending-down'} 
              size={16} 
              color={trend === 'up' ? theme.colors.success : theme.colors.error} 
            />
          </View>
        )}
      </View>
    </View>
  );

  const renderOverviewTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => loadData(true)} />
      }
    >
      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        {renderSummaryCard(
          'Total Income',
          formatCurrency(summary.totalIncome),
          'trending-up',
          theme.colors.success,
          'up'
        )}
        {renderSummaryCard(
          'Total Expenses',
          formatCurrency(summary.totalExpenses),
          'trending-down',
          theme.colors.error,
          'down'
        )}
        {renderSummaryCard(
          'Net Profit',
          formatCurrency(summary.netProfit),
          'calculator',
          summary.netProfit >= 0 ? theme.colors.info : theme.colors.error
        )}
        {renderSummaryCard(
          'Negative Balances',
          summary.driversWithNegativeBalance.toString(),
          'alert-triangle',
          theme.colors.warning
        )}
      </View>

      {/* Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.actionGrid}>
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.primary }]}
            onPress={() => navigation?.navigate('DataImport')}
          >
            <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>Import Data</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.success }]}
            onPress={() => setActiveTab('transactions')}
          >
            <Ionicons name="list" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>View Transactions</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.info }]}
            onPress={() => setActiveTab('balances')}
          >
            <Ionicons name="people" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>Driver Balances</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.warning }]}
            onPress={() => Alert.alert('Feature Coming Soon', 'Reports will be available in the next update')}
          >
            <Ionicons name="bar-chart" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>Reports</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.purple || '#8B5CF6' }]}
            onPress={handleImportRealData}
          >
            <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>Import Data</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.gray600 || '#6B7280' }]}
            onPress={handleRecalculateBalances}
          >
            <Ionicons name="refresh" size={20} color={theme.colors.white} />
            <Text style={styles.actionButtonText}>Recalculate</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recent Transactions */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Recent Transactions</Text>
          <TouchableOpacity onPress={() => setActiveTab('transactions')}>
            <Text style={[styles.linkText, { color: theme.colors.primary }]}>View All</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.transactionsList}>
          {transactions.slice(0, 5).map((transaction) => (
            <TouchableOpacity 
              key={transaction.id} 
              style={styles.transactionItem}
              onPress={() => navigation?.navigate('TransactionDetails', { transactionId: transaction.id })}
            >
              <View style={styles.transactionLeft}>
                <View style={[styles.transactionIcon, { backgroundColor: getTypeColor(transaction.type) + '20' }]}>
                  <Ionicons 
                    name={transaction.type === 'income' ? 'add' : 'remove'} 
                    size={16} 
                    color={getTypeColor(transaction.type)} 
                  />
                </View>
                <View style={styles.transactionDetails}>
                  <Text style={styles.transactionCategory}>{transaction.category}</Text>
                  <Text style={styles.transactionDate}>
                    {new Date(transaction.transactionDate).toLocaleDateString()}
                  </Text>
                </View>
              </View>
              <View style={styles.transactionRight}>
                <Text style={[styles.transactionAmount, { color: getTypeColor(transaction.type) }]}>
                  {transaction.type === 'expense' ? '-' : '+'}
                  {formatCurrency(transaction.amount)}
                </Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(transaction.status) + '20' }]}>
                  <Text style={[styles.statusText, { color: getStatusColor(transaction.status) }]}>
                    {transaction.status}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderImportTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => loadData(true)} />
      }
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>CSV Data Import</Text>
        <Text style={styles.sectionDescription}>
          Import real business data from CSV files. Supports Careem payments, Uber earnings, traffic fines, and Salik tolls.
        </Text>
        
        {/* Import/Export Information */}
        <View style={styles.infoCard}>
          <View style={styles.infoHeader}>
            <Ionicons name="information-circle" size={24} color={theme.colors.info} />
            <Text style={styles.infoTitle}>Data Import/Export</Text>
          </View>
          <Text style={styles.infoText}>
            All data import and export functions have been moved to the dedicated Import/Export section. 
            Use the sidebar navigation to access comprehensive tools for importing Uber, Careem, Salik, and fine data with advanced duplicate detection.
          </Text>
        </View>

        {/* Quick Actions */}
        <View style={styles.quickActions}>
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.info }]}
            onPress={handleRecalculateBalances}
          >
            <Ionicons name="refresh" size={20} color="white" />
            <Text style={styles.actionButtonText}>Recalculate Balances</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: theme.colors.success }]}
            onPress={handleImportRealData}
          >
            <Ionicons name="checkmark-circle" size={20} color="white" />
            <Text style={styles.actionButtonText}>Initialize System</Text>
          </TouchableOpacity>
        </View>

        {/* Import Status */}
        {loading && (
          <View style={styles.importStatus}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Text style={styles.importStatusText}>Processing import...</Text>
          </View>
        )}
      </View>

      {/* Recent Imports */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Imports</Text>
        <View style={styles.importHistoryList}>
          {/* Mock import history - in real app would load from getImportBatches() */}
          <View style={styles.importHistoryItem}>
            <View style={styles.importHistoryIcon}>
              <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
            </View>
            <View style={styles.importHistoryDetails}>
              <Text style={styles.importHistoryTitle}>Careem Weekly Payments</Text>
              <Text style={styles.importHistoryDate}>Today, 2:30 PM</Text>
            </View>
            <Text style={styles.importHistoryCount}>45 records</Text>
          </View>

          <View style={styles.importHistoryItem}>
            <View style={styles.importHistoryIcon}>
              <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
            </View>
            <View style={styles.importHistoryDetails}>
              <Text style={styles.importHistoryTitle}>Traffic Fines</Text>
              <Text style={styles.importHistoryDate}>Yesterday, 4:15 PM</Text>
            </View>
            <Text style={styles.importHistoryCount}>12 records</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderTransactionsTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => loadData(true)} />
      }
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>All Transactions ({transactions.length})</Text>
        
        <View style={styles.transactionsList}>
          {transactions.map((transaction) => (
            <TouchableOpacity 
              key={transaction.id} 
              style={styles.transactionItemFull}
              onPress={() => navigation?.navigate('TransactionDetails', { transactionId: transaction.id })}
            >
              <View style={styles.transactionHeader}>
                <View style={styles.transactionLeft}>
                  <View style={[styles.transactionIcon, { backgroundColor: getTypeColor(transaction.type) + '20' }]}>
                    <Ionicons 
                      name={transaction.type === 'income' ? 'add' : 'remove'} 
                      size={16} 
                      color={getTypeColor(transaction.type)} 
                    />
                  </View>
                  <View style={styles.transactionDetails}>
                    <Text style={styles.transactionCategory}>{transaction.category}</Text>
                    <Text style={styles.transactionDate}>
                      {new Date(transaction.transactionDate).toLocaleDateString()}
                    </Text>
                  </View>
                </View>
                <View style={styles.transactionRight}>
                  <Text style={[styles.transactionAmount, { color: getTypeColor(transaction.type) }]}>
                    {transaction.type === 'expense' ? '-' : '+'}
                    {formatCurrency(transaction.amount)}
                  </Text>
                  <View style={[styles.statusBadge, { backgroundColor: getStatusColor(transaction.status) + '20' }]}>
                    <Text style={[styles.statusText, { color: getStatusColor(transaction.status) }]}>
                      {transaction.status}
                    </Text>
                  </View>
                </View>
              </View>
              
              {transaction.description && (
                <Text style={styles.transactionDescription}>{transaction.description}</Text>
              )}
              
              {transaction.referenceNumber && (
                <Text style={styles.transactionReference}>Ref: {transaction.referenceNumber}</Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderBalancesTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => loadData(true)} />
      }
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Driver Balances ({driverBalances.length})</Text>
        
        {/* Table Header */}
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver Name</Text>
            <Text style={[styles.tableHeaderCell, styles.balanceColumn]}>Balance</Text>
            <Text style={[styles.tableHeaderCell, styles.incomeColumn]}>Income</Text>
            <Text style={[styles.tableHeaderCell, styles.rentColumn]}>Company Share</Text>
            <Text style={[styles.tableHeaderCell, styles.finesColumn]}>Fines</Text>
            <Text style={[styles.tableHeaderCell, styles.totalOwedColumn]}>Total Owed</Text>
          </View>
          
          {/* Table Rows */}
          <ScrollView style={styles.tableBody}>
            {driverBalances.map((balance, index) => (
              <TouchableOpacity 
                key={balance.driverId} 
                style={[
                  styles.tableRow,
                  index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
                ]}
                onPress={() => handleBalancePress(balance)}
              >
                <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={1}>
                  {balance.driver?.name || `Driver ${balance.driverId.slice(0, 8)}`}
                </Text>
                <Text style={[
                  styles.tableCell, 
                  styles.balanceColumn,
                  { color: balance.currentBalance >= 0 ? theme.colors.success : theme.colors.error }
                ]}>
                  {formatCurrency(balance.currentBalance)}
                </Text>
                <Text style={[styles.tableCell, styles.incomeColumn, { color: theme.colors.success }]}>
                  {formatCurrency((balance.totalCareemIncome || 0) + (balance.totalUberIncome || 0))}
                </Text>
                <Text style={[styles.tableCell, styles.rentColumn, { color: theme.colors.warning }]}>
                  {formatCurrency(balance.totalRentCharged || 0)}
                </Text>
                <Text style={[styles.tableCell, styles.finesColumn, { color: theme.colors.error }]}>
                  {formatCurrency(balance.totalFines || 0)}
                </Text>
                <Text style={[styles.tableCell, styles.totalOwedColumn]}>
                  {formatCurrency(balance.totalOwed)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </ScrollView>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'import':
        return renderImportTab();
      case 'transactions':
        return renderTransactionsTab();
      case 'balances':
        return renderBalancesTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading accounting data...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="calculator" size={24} color={theme.colors.primary} />
          <Text style={styles.headerTitle}>Accounting</Text>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScroll}>
          {[
            { key: 'overview', label: 'Overview', icon: 'home' },
            { key: 'import', label: 'Import', icon: 'cloud-upload' },
            { key: 'transactions', label: 'Transactions', icon: 'list' },
            { key: 'balances', label: 'Balances', icon: 'people' }
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tabButton,
                activeTab === tab.key && styles.tabButtonActive
              ]}
              onPress={() => setActiveTab(tab.key as any)}
            >
              <Ionicons 
                name={tab.icon as any} 
                size={16} 
                color={activeTab === tab.key ? theme.colors.primary : theme.colors.gray400} 
              />
              <Text style={[
                styles.tabButtonText,
                activeTab === tab.key && styles.tabButtonTextActive
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Balance Details Modal */}
      <Modal
        visible={balanceModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setBalanceModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <View style={styles.modalHeaderLeft}>
              <Text style={styles.modalTitle}>
                {selectedBalance?.driver?.name || 'Driver Balance Details'}
              </Text>
            </View>
            <View style={styles.modalHeaderRight}>
              {/* Payment Button - show for all non-zero balances */}
              {selectedBalance && selectedBalance.currentBalance !== 0 && (
                <TouchableOpacity
                  style={[
                    styles.paymentButton,
                    selectedBalance.currentBalance > 0 && styles.payoutButton
                  ]}
                  onPress={handleOpenPaymentModal}
                >
                  <Ionicons 
                    name={selectedBalance.currentBalance < 0 ? "card" : "cash"} 
                    size={20} 
                    color={theme.colors.white} 
                  />
                  <Text style={styles.paymentButtonText}>
                    {selectedBalance.currentBalance < 0 ? "Record Payment" : "Record Payout"}
                  </Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setBalanceModalVisible(false)}
              >
                <Ionicons name="close" size={24} color={theme.colors.gray600} />
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView style={styles.modalContent}>
            {selectedBalance && (
              <>
                {/* Current Balance */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Current Balance</Text>
                  <View style={styles.modalBalanceCard}>
                    <Text style={[
                      styles.modalBalanceAmount,
                      { color: selectedBalance.currentBalance >= 0 ? theme.colors.success : theme.colors.error }
                    ]}>
                      {formatCurrency(selectedBalance.currentBalance)}
                    </Text>
                    <Text style={styles.modalBalanceLabel}>
                      {selectedBalance.currentBalance >= 0 ? 'Amount Due to Driver' : 'Amount Driver Owes'}
                    </Text>
                  </View>
                </View>

                {/* Income Breakdown */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Income Breakdown</Text>
                  <View style={styles.modalBreakdownCard}>
                    <View style={styles.modalBreakdownRow}>
                      <Text style={styles.modalBreakdownLabel}>Careem Earnings:</Text>
                      <Text style={styles.modalBreakdownValue}>
                        {formatCurrency(selectedBalance.totalCareemIncome)}
                      </Text>
                    </View>
                    <View style={styles.modalBreakdownRow}>
                      <Text style={styles.modalBreakdownLabel}>Uber Earnings:</Text>
                      <Text style={styles.modalBreakdownValue}>
                        {formatCurrency(selectedBalance.totalUberIncome)}
                      </Text>
                    </View>
                    <View style={[styles.modalBreakdownRow, styles.modalBreakdownTotal]}>
                      <Text style={styles.modalBreakdownTotalLabel}>Total Earned:</Text>
                      <Text style={[styles.modalBreakdownValue, styles.modalBreakdownTotalValue]}>
                        {formatCurrency((selectedBalance.totalCareemIncome || 0) + (selectedBalance.totalUberIncome || 0))}
                      </Text>
                    </View>
                    
                    {/* Show detailed breakdown if available */}
                    {selectedBalance.detailedBreakdown?.income && selectedBalance.detailedBreakdown.income.details.length > 0 && (
                      <View style={styles.expandedBreakdownContent}>
                        <Text style={styles.detailedBreakdownTitle}>Detailed Income History:</Text>
                        {selectedBalance.detailedBreakdown.income.details.map((item, index) => (
                          <View key={index} style={styles.detailedBreakdownItem}>
                            <Text style={styles.detailedItemDescription}>{item.description}</Text>
                            <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                </View>

                {/* Deductions Breakdown */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Deductions Breakdown</Text>
                  
                  {/* Rent Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowRentBreakdown(!showRentBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Company Share:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedBalance.totalRentCharged)}
                        </Text>
                        <Ionicons 
                          name={showRentBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color={theme.colors.gray500} 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showRentBreakdown && selectedBalance.detailedBreakdown?.rent && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedBalance.detailedBreakdown.rent.details.map((item, index) => (
                          <View key={index} style={styles.detailedBreakdownItem}>
                            <View style={styles.detailedItemContent}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemSubtext}>Vehicle: {item.vehicle}</Text>
                            </View>
                            <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>

                  {/* Salik Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowSalikBreakdown(!showSalikBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Salik Tolls:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedBalance.totalSalik)}
                        </Text>
                        <Ionicons 
                          name={showSalikBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color={theme.colors.gray500} 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showSalikBreakdown && selectedBalance.detailedBreakdown?.salik && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedBalance.detailedBreakdown.salik.details.map((item, index) => (
                          <View key={index} style={styles.detailedBreakdownItem}>
                            <View style={styles.detailedItemContent}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemSubtext}>Period: {item.period}</Text>
                            </View>
                            <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>

                  {/* Fines Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowFinesBreakdown(!showFinesBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Traffic Fines:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedBalance.totalFines)}
                        </Text>
                        <Ionicons 
                          name={showFinesBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color={theme.colors.gray500} 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showFinesBreakdown && selectedBalance.detailedBreakdown?.fines && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedBalance.detailedBreakdown.fines.details.map((item, index) => (
                          <View key={index} style={styles.detailedBreakdownItem}>
                            <View style={styles.detailedItemContent}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemSubtext}>Period: {item.period}</Text>
                            </View>
                            <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>

                  <View style={styles.modalBreakdownCard}>
                    <View style={styles.modalBreakdownRow}>
                      <Text style={styles.modalBreakdownLabel}>Other Expenses:</Text>
                      <Text style={styles.modalBreakdownValue}>
                        {formatCurrency(selectedBalance.totalOtherExpenses)}
                      </Text>
                    </View>
                    <View style={[styles.modalBreakdownRow, styles.modalBreakdownTotal]}>
                      <Text style={styles.modalBreakdownTotalLabel}>Total Deductions:</Text>
                      <Text style={[styles.modalBreakdownValue, styles.modalBreakdownTotalValue]}>
                        {formatCurrency(selectedBalance.totalOwed)}
                      </Text>
                    </View>
                  </View>
                </View>

                {/* Company Share Details Section */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Company Share Details</Text>
                  {loadingRentDetails ? (
                    <View style={styles.rentLoadingContainer}>
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                      <Text style={styles.rentLoadingText}>Loading rent details...</Text>
                    </View>
                  ) : rentDetails ? (
                    <View style={styles.modalBreakdownCard}>
                      {/* Rent Summary */}
                      <View style={styles.rentSummaryContainer}>
                        <View style={styles.modalBreakdownRow}>
                          <Text style={styles.modalBreakdownLabel}>Total Days:</Text>
                          <Text style={styles.modalBreakdownValue}>
                            {rentDetails.summary.totalDays} days
                          </Text>
                        </View>
                        <View style={styles.modalBreakdownRow}>
                          <Text style={styles.modalBreakdownLabel}>Vehicles Used:</Text>
                          <Text style={styles.modalBreakdownValue}>
                            {rentDetails.summary.uniqueVehicles} vehicle{rentDetails.summary.uniqueVehicles !== 1 ? 's' : ''}
                          </Text>
                        </View>
                        <View style={[styles.modalBreakdownRow, styles.modalBreakdownTotal]}>
                          <Text style={styles.modalBreakdownTotalLabel}>Total Company Share:</Text>
                          <Text style={[styles.modalBreakdownValue, styles.modalBreakdownTotalValue]}>
                            {formatCurrency(rentDetails.summary.totalAmount)}
                          </Text>
                        </View>
                      </View>

                      {/* Individual Vehicle Details */}
                      {rentDetails.rentDetails.map((detail: any, index: number) => (
                        <View key={index} style={styles.vehicleDetailContainer}>
                          <View style={styles.vehicleHeaderRow}>
                            <Text style={styles.vehicleTitle}>
                              {detail.licensePlate} - {detail.makeModel}
                            </Text>
                            <Text style={[
                              styles.vehicleStatus,
                              { color: detail.status === 'active' ? theme.colors.success : theme.colors.gray500 }
                            ]}>
                              {detail.status === 'active' ? 'Active' : 'Completed'}
                            </Text>
                          </View>
                          <View style={styles.vehicleDetailsRow}>
                            <Text style={styles.vehicleDetailText}>
                              Period: {new Date(detail.startDate).toLocaleDateString()} - {new Date(detail.endDate).toLocaleDateString()}
                            </Text>
                          </View>
                          <View style={styles.vehicleDetailsRow}>
                            <Text style={styles.vehicleDetailText}>
                              {detail.days} days × {formatCurrency(detail.dailyRate)}/day = {formatCurrency(detail.totalAmount)}
                            </Text>
                          </View>
                          <View style={styles.vehicleDetailsRow}>
                            <Text style={styles.vehicleDetailText}>
                              Source: {detail.source === 'current' ? 'Current Assignment' : 'Assignment History'}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <View style={styles.rentErrorContainer}>
                      <Text style={styles.rentErrorText}>Unable to load rent details</Text>
                    </View>
                  )}
                </View>

                {/* Calculation Summary */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Calculation</Text>
                  <View style={styles.modalCalculationCard}>
                    <View style={styles.modalCalculationRow}>
                      <Text style={styles.modalCalculationLabel}>Total Earned:</Text>
                      <Text style={[styles.modalCalculationValue, { color: theme.colors.success }]}>
                        +{formatCurrency((selectedBalance.totalCareemIncome || 0) + (selectedBalance.totalUberIncome || 0))}
                      </Text>
                    </View>
                    <View style={styles.modalCalculationRow}>
                      <Text style={styles.modalCalculationLabel}>Total Deductions:</Text>
                      <Text style={[styles.modalCalculationValue, { color: theme.colors.error }]}>
                        -{formatCurrency(selectedBalance.totalRentCharged + selectedBalance.totalFines + selectedBalance.totalSalik)}
                      </Text>
                    </View>
                    <View style={styles.modalCalculationSeparator} />
                    <View style={styles.modalCalculationRow}>
                      <Text style={styles.modalCalculationFinalLabel}>Final Balance:</Text>
                      <Text style={[
                        styles.modalCalculationFinalValue,
                        { color: selectedBalance.currentBalance >= 0 ? theme.colors.success : theme.colors.error }
                      ]}>
                        {formatCurrency(selectedBalance.currentBalance)}
                      </Text>
                    </View>
                  </View>
                </View>

                {/* Last Updated */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLastUpdated}>
                    Last calculated: {selectedBalance.lastCalculationDate 
                      ? new Date(selectedBalance.lastCalculationDate).toLocaleDateString()
                      : 'Not available'
                    }
                  </Text>
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </Modal>

      {/* Driver Payment Modal */}
      {selectedBalance && (
        <DriverPaymentModalEnhanced
          isVisible={paymentModalVisible}
          onClose={() => setPaymentModalVisible(false)}
          driverBalance={{
            driver_id: selectedBalance.driverId,
            drivers: { name: selectedBalance.driver?.name || 'Unknown Driver' },
            current_balance: selectedBalance.currentBalance
          }}
          onPaymentSuccess={handlePaymentRecorded}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  headerTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  tabNavigation: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  tabScroll: {
    paddingHorizontal: theme.spacing.md,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginRight: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  tabButtonActive: {
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary,
  },
  tabButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray400,
  },
  tabButtonTextActive: {
    color: theme.colors.primary,
    fontWeight: '500' as any,
  },
  tabContent: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
  },
  summaryGrid: {
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  summaryCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryContent: {
    gap: theme.spacing.sm,
  },
  summaryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryTitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
  },
  trendContainer: {
    alignSelf: 'flex-start',
  },
  section: {
    padding: theme.spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  linkText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
  },
  actionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
    minWidth: '30%',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
  },
  actionButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
  },
  transactionsList: {
    gap: theme.spacing.sm,
  },
  transactionItem: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transactionItemFull: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  transactionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transactionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flex: 1,
  },
  transactionIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  transactionDetails: {
    flex: 1,
  },
  transactionCategory: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  transactionDate: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  transactionRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  transactionAmount: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '500' as any,
    textTransform: 'capitalize',
  },
  transactionDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: theme.spacing.sm,
  },
  transactionReference: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray500,
    marginTop: 4,
  },
  balancesList: {
    gap: theme.spacing.md,
  },
  balanceItem: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  balanceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  balanceDriver: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  balanceAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.colors.gray100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  balanceDriverName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  balanceAmount: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
  },
  balanceDetails: {
    gap: 8,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  balanceValue: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  comingSoonContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
  },
  comingSoonTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  comingSoonDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: theme.spacing.xl,
  },
  comingSoonButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  comingSoonButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  infoCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    margin: theme.spacing.md,
    padding: theme.spacing.lg,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.info,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  infoTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  infoText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    lineHeight: 22,
  },
  quickActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
  },
  importStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
  },
  importStatusText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  importHistoryList: {
    gap: theme.spacing.sm,
  },
  importHistoryItem: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  importHistoryIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.colors.success + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  importHistoryDetails: {
    flex: 1,
  },
  importHistoryTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  importHistoryDate: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  importHistoryCount: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.primary,
  },

  // Table Styles
  tableContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    marginTop: theme.spacing.md,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  tableHeaderCell: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.gray700,
    textAlign: 'center',
  },
  tableBody: {
    maxHeight: 400,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  tableRowEven: {
    backgroundColor: theme.colors.white,
  },
  tableRowOdd: {
    backgroundColor: theme.colors.gray50,
  },
  tableCell: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  driverNameColumn: {
    flex: 2.5,
    textAlign: 'left',
  },
  balanceColumn: {
    flex: 1.5,
    fontWeight: '600' as any,
  },
  incomeColumn: {
    flex: 1.3,
  },
  rentColumn: {
    flex: 1.3,
  },
  finesColumn: {
    flex: 1.2,
  },
  totalOwedColumn: {
    flex: 1.5,
  },
  // Legacy column styles for backward compatibility
  earnedColumn: {
    flex: 1.2,
  },
  owedColumn: {
    flex: 1.2,
  },

  // Modal Styles
  modalContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  modalHeaderLeft: {
    flex: 1,
  },
  modalHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  modalTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  paymentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.success,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  payoutButton: {
    backgroundColor: '#FF6B35', // Orange color for payouts
  },
  paymentButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  modalCloseButton: {
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.full,
  },
  modalContent: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  modalSection: {
    marginBottom: theme.spacing.xl,
  },
  modalSectionTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  modalBalanceCard: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  modalBalanceAmount: {
    fontSize: 28,
    fontWeight: '700' as any,
    marginBottom: theme.spacing.xs,
  },
  modalBalanceLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  modalBreakdownCard: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  modalBreakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  modalBreakdownLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  modalBreakdownValue: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  modalBreakdownTotal: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    marginTop: theme.spacing.sm,
    paddingTop: theme.spacing.md,
  },
  modalBreakdownTotalLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  modalBreakdownTotalValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '700' as any,
  },
  modalCalculationCard: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  modalCalculationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  modalCalculationLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  modalCalculationValue: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
  },
  modalCalculationSeparator: {
    height: 1,
    backgroundColor: theme.colors.gray200,
    marginVertical: theme.spacing.md,
  },
  modalCalculationFinalLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  modalCalculationFinalValue: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
  },
  modalLastUpdated: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray500,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  // Rent Details Styles
  rentLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    gap: theme.spacing.sm,
  },
  rentLoadingText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  rentSummaryContainer: {
    marginBottom: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  vehicleDetailContainer: {
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  vehicleHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  vehicleTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    flex: 1,
  },
  vehicleStatus: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '500' as any,
    textTransform: 'uppercase',
  },
  vehicleDetailsRow: {
    marginBottom: theme.spacing.xs,
  },
  vehicleDetailText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray600,
  },
  rentErrorContainer: {
    padding: theme.spacing.lg,
    alignItems: 'center',
  },
  rentErrorText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray500,
    fontStyle: 'italic',
  },
  // Detailed Breakdown Styles
  detailedBreakdownHeader: {
    paddingBottom: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  detailedBreakdownTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  expandableBreakdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  expandableBreakdownRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  expandedBreakdownContent: {
    paddingTop: theme.spacing.sm,
    paddingLeft: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray100,
  },
  detailedBreakdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray50,
  },
  detailedItemContent: {
    flex: 1,
    marginRight: theme.spacing.md,
  },
  detailedItemDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
    marginBottom: theme.spacing.xs,
  },
  detailedItemSubtext: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray600,
  },
  detailedItemAmount: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
});
